
import React, { useState, useEffect } from 'react';
import { Users, Coins, Loader2, Search, ArrowUpRight, ArrowDownLeft, Trash2, RefreshCw, ShieldCheck } from 'lucide-react';
import { apiService } from '../services/apiService';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';

interface UserData {
  uid: string;
  email: string;
  displayName: string;
  credits: number;
  createdAt: any;
}

export const Admin: React.FC = () => {
  const { addToast } = useToast();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [creditReason, setCreditReason] = useState('');
  const [isUpdatingCredits, setIsUpdatingCredits] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await apiService.getAdminUsers();
      if (res.success) setUsers(res.users);
    } catch (e: any) {
      addToast("Failed to fetch users: " + e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCredits = async () => {
    if (!selectedUser || !creditAmount) return;
    setIsUpdatingCredits(true);
    try {
      await apiService.updateAdminCredits(selectedUser.uid, creditAmount, creditReason || 'Admin adjustment');
      addToast(`Updated ${selectedUser.displayName}'s credits by ${creditAmount}`, 'success');
      
      // Update local state
      setUsers(prev => prev.map(u => {
          if (u.uid === selectedUser.uid) {
              return { ...u, credits: u.credits + creditAmount };
          }
          return u;
      }));
      
      setSelectedUser(null);
      setCreditAmount(0);
      setCreditReason('');
    } catch (e: any) {
      addToast("Failed to update credits: " + e.message, 'error');
    } finally {
      setIsUpdatingCredits(false);
    }
  };

  const handleCleanup = async () => {
      setIsCleaning(true);
      try {
          await apiService.triggerCleanup(50);
          addToast("Maintenance cleanup job queued successfully", 'success');
      } catch (e: any) {
          addToast("Cleanup failed: " + e.message, 'error');
      } finally {
          setIsCleaning(false);
      }
  };

  const filteredUsers = users.filter(u => 
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
    (u.displayName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.uid || '').includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User List Panel */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-[700px]">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center">
             <div className="flex items-center gap-2">
                <Users size={20} className="text-slate-500" />
                <h3 className="font-bold text-slate-800">User Management</h3>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{users.length} Total</span>
             </div>
             <button onClick={fetchUsers} className="p-2 hover:bg-slate-50 rounded-full text-slate-400">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
             </button>
          </div>
          
          <div className="p-4 bg-slate-50 border-b border-slate-100">
             <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                    type="text" 
                    placeholder="Search users by name, email or UID..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
             </div>
          </div>

          <div className="flex-1 overflow-y-auto">
             {loading ? (
                 <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
                    <Loader2 className="animate-spin" size={32} />
                    <p>Loading users...</p>
                 </div>
             ) : (
                 <table className="w-full text-left text-sm border-collapse">
                    <thead className="bg-slate-50 sticky top-0 z-10 font-bold text-slate-700 text-xs uppercase tracking-wider">
                       <tr>
                          <th className="px-4 py-3 border-b">User</th>
                          <th className="px-4 py-3 border-b text-center">Credits</th>
                          <th className="px-4 py-3 border-b text-right">Actions</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                       {filteredUsers.map(u => (
                          <tr key={u.uid} className={`hover:bg-slate-50 transition-colors ${selectedUser?.uid === u.uid ? 'bg-primary-50' : ''}`}>
                             <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                   <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-xs shrink-0">
                                      {u.displayName ? u.displayName[0] : 'U'}
                                   </div>
                                   <div className="min-w-0">
                                      <p className="font-bold text-slate-900 truncate">{u.displayName || 'Unknown'}</p>
                                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                   </div>
                                </div>
                             </td>
                             <td className="px-4 py-3 text-center">
                                <span className={`font-mono font-bold ${u.credits < 50 ? 'text-amber-600' : 'text-slate-900'}`}>{u.credits}</span>
                             </td>
                             <td className="px-4 py-3 text-right">
                                <button 
                                    onClick={() => setSelectedUser(u)}
                                    className="text-xs font-bold text-primary-600 hover:text-primary-800 hover:bg-primary-50 px-3 py-1.5 rounded-md transition-colors"
                                >
                                   Adjust Credits
                                </button>
                             </td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
             )}
          </div>
        </div>

        {/* Action Panel */}
        <div className="space-y-6">
           {/* Credit Adjustment Tool */}
           <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
                 <Coins size={18} className="text-amber-500" /> Credit Adjustment
              </h3>
              
              {selectedUser ? (
                 <div className="space-y-4 animate-fade-in">
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                       <p className="text-xs text-slate-500 uppercase font-bold mb-1">Target User</p>
                       <p className="text-sm font-bold text-slate-900">{selectedUser.displayName}</p>
                       <p className="text-xs text-slate-500">{selectedUser.email}</p>
                       <p className="text-xs font-mono mt-1 bg-white px-2 py-0.5 rounded border border-slate-100 inline-block">{selectedUser.uid}</p>
                    </div>
                    
                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Amount (+ to add, - to remove)</label>
                       <div className="relative">
                          {creditAmount > 0 ? <ArrowDownLeft className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500" size={16} /> : creditAmount < 0 ? <ArrowUpRight className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" size={16} /> : null}
                          <input 
                             type="number" 
                             value={creditAmount}
                             onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                             className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          />
                       </div>
                    </div>

                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Reason / Description</label>
                       <input 
                          type="text" 
                          placeholder="e.g. Bug reward, Custom purchase, etc."
                          value={creditReason}
                          onChange={(e) => setCreditReason(e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm"
                       />
                    </div>

                    <div className="flex gap-2">
                       <Button variant="secondary" onClick={() => setSelectedUser(null)} className="flex-1">Cancel</Button>
                       <Button onClick={handleUpdateCredits} isLoading={isUpdatingCredits} className="flex-1">Update Credits</Button>
                    </div>
                 </div>
              ) : (
                 <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                    <p className="text-sm">Select a user from the list to adjust their balance.</p>
                 </div>
              )}
           </div>

           {/* Maintenance Tools */}
           <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
                 <ShieldCheck size={18} className="text-indigo-500" /> Maintenance
              </h3>
              
              <div className="space-y-4">
                 <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
                    <h4 className="text-sm font-bold text-amber-800 mb-1">Veo Project Cleanup</h4>
                    <p className="text-xs text-amber-700 mb-4 leading-relaxed">
                       Queue a maintenance task to delete up to 50 old projects from the Google Labs Flow library. Use this if the automatic cleanup fails.
                    </p>
                    <Button 
                        onClick={handleCleanup} 
                        isLoading={isCleaning} 
                        variant="danger" 
                        className="w-full text-xs py-2 bg-amber-600 hover:bg-amber-700 border-none"
                    >
                       Trigger Manual Cleanup
                    </Button>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};
